import { Link } from 'react-router-dom';
import type { AccessInfo } from '../api/types';
import { Perm } from '../api/types';

const PERM_CHIPS = [
  { flag: Perm.READ, label: 'R', title: 'read', className: 'bg-green-100 text-green-800' },
  { flag: Perm.WRITE, label: 'W', title: 'write', className: 'bg-blue-100 text-blue-800' },
  { flag: Perm.LIST, label: 'L', title: 'list', className: 'bg-purple-100 text-purple-800' },
  { flag: Perm.DELETE, label: 'D', title: 'delete', className: 'bg-red-100 text-red-800' },
] as const;

/** Per-permission chips (R/W/L/D) with distinct colors. */
export function PermChips({ perms }: { perms: number }) {
  const active = PERM_CHIPS.filter((c) => (perms & c.flag) !== 0);
  if (active.length === 0) return <span className="text-xs text-gray-400">—</span>;
  return (
    <span className="inline-flex shrink-0 gap-1">
      {active.map((c) => (
        <span
          key={c.label}
          title={c.title}
          className={`rounded px-1 py-0.5 text-[10px] font-semibold leading-none ${c.className}`}
        >
          {c.label}
        </span>
      ))}
    </span>
  );
}

/** The grant's path pattern (glob), or "whole bucket" when blank. */
function Scope({ pattern }: { pattern: string }) {
  return pattern ? (
    <span className="block truncate font-mono text-[11px] text-gray-500" title={`Grant scope: ${pattern}`}>
      {pattern}
    </span>
  ) : (
    <span className="block text-[11px] italic text-gray-400">whole bucket</span>
  );
}

/**
 * Who can reach an object/folder: public grants first (amber), then ACL
 * principals with a type icon, name, grant scope, and permission chips.
 * Read-only — grants are managed on the ACL page.
 */
export function AccessList({ info, showAclLink }: { info: AccessInfo; showAclLink?: boolean }) {
  const empty = info.acl.length + info.public.length === 0;
  return (
    <div>
      {empty ? (
        <p className="text-sm text-gray-500">
          Admin-only — no API key, user, or public grant covers this.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {info.public.map((g) => (
            <li
              key={g.id}
              className="flex items-center gap-2.5 rounded border border-amber-200 bg-amber-50 px-2.5 py-1.5"
            >
              <span aria-hidden className="text-base leading-none">🌐</span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-amber-800">Public (anonymous)</span>
                <Scope pattern={g.pathPrefix} />
              </span>
              <PermChips perms={g.perms} />
            </li>
          ))}
          {info.acl.map((p) => {
            const isApiKey = p.principalType === 'apikey';
            const icon = isApiKey ? '🔑' : p.principal ? '👤' : '🏢';
            const kind = isApiKey ? 'API key' : p.principal ? 'user' : 'tenant-wide';
            return (
              <li
                key={p.entryId + p.pathPrefix}
                className="flex items-center gap-2.5 rounded border border-gray-200 bg-gray-50 px-2.5 py-1.5"
              >
                <span aria-hidden className="text-base leading-none">{icon}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm">
                    <span className="font-medium text-gray-800">{p.name}</span>
                    <span className="ml-1.5 text-xs text-gray-500">
                      {kind}
                      {p.principal ? ` · ${p.principal}` : ''}
                    </span>
                  </span>
                  <Scope pattern={p.pathPrefix} />
                </span>
                <PermChips perms={p.perms} />
              </li>
            );
          })}
        </ul>
      )}
      {showAclLink ? (
        <Link to="/acl" className="mt-2 inline-block text-xs text-navy hover:underline">
          Manage grants on the ACL page →
        </Link>
      ) : null}
    </div>
  );
}
